import { createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { SortOrder } from "antd/es/table/interface";
import * as accessControlAPI from "services/accessControl";
import { RootState } from "app/store";
import { EmailDomainListResponse } from "types/dto/response/accessControl";
import { EmailDomainListParams } from "types/dto/request/accessControl";
import Page from "types/page";
import { getOrder } from "utils";

interface IState {
  currentPage: number;
  currentSize: number;
  totalElements: number;
}

export const fetchEmailDomains = createAsyncThunk<
  Page<EmailDomainListResponse>,
  Omit<EmailDomainListParams, "sort" | "size"> & { sortKey?: string; order?: SortOrder }
>("accessControl/fetchAll", ({ sortKey, order, ...params }) => {
  const sort = sortKey && order ? sortKey + "," + getOrder(order) : "lastModifiedDate,DESC";
  return accessControlAPI.fetchEmailDomains({
    sort,
    size: 10,
    ...params,
    page: params.page ? params.page - 1 : undefined,
  });
});

export const fetchEmailDomain = createAsyncThunk("accessControl/fetchOne", (id: number) =>
  accessControlAPI.fetchEmailDomain(id)
);

export const createEmailDomain = createAsyncThunk("accessControl/create", accessControlAPI.createEmailDomain);

export const updateEmailDomain = createAsyncThunk(
  "accessControl/update",
  ({ id, ...data }: { id: number; emailDomain: string; blockRegistration: boolean; blockLogin: boolean }) =>
    accessControlAPI.updateEmailDomain(id, data)
);

export const removeEmailDomain = createAsyncThunk("accessControl/delete", accessControlAPI.deleteEmailDomain);

const emailDomainAdapter = createEntityAdapter<EmailDomainListResponse>();

const accessControlSlice = createSlice({
  name: "accessControl",
  initialState: emailDomainAdapter.getInitialState<IState>({
    currentPage: 1,
    currentSize: 10,
    totalElements: 0,
  }),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchEmailDomains.fulfilled, (state, { payload }) => {
      const { content, totalElements, page, size } = payload;
      emailDomainAdapter.setAll(state, content);
      state.currentPage = page + 1;
      state.currentSize = size;
      state.totalElements = totalElements;
    });
    builder.addCase(fetchEmailDomain.fulfilled, (state, { payload }) => {
      emailDomainAdapter.setOne(state, payload);
    });
    builder.addCase(updateEmailDomain.fulfilled, (state, { payload }) => {
      emailDomainAdapter.setOne(state, payload);
    });
  },
});

export default accessControlSlice.reducer;

export const emailDomainSelectors = emailDomainAdapter.getSelectors<RootState>((state) => state.accessControl);
